import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Search, Filter, X, RefreshCw, Users, AlertTriangle, Eye, Archive, Trash2, Phone, MapPin, ChevronRight } from "lucide-react";
import getApiUrl from './utils/getApiUrl';

const API = `${getApiUrl()}/api`;

// Composant Input avec Autocomplete enrichi (clients avec infos completes)
function AutocompleteInput({ value, onChange, placeholder, testId, onClientSelect }) {
  const [clients, setClients] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  
  const fetchClients = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setClients([]);
      setShowDropdown(false);
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.get(`${API}/search/autocomplete`, {
        params: { q: query, field: "nom", limit: 10 }
      });
      const newClients = res.data.clients || [];
      setClients(newClients);
      if (newClients.length > 0) setShowDropdown(true);
    } catch (err) {
      console.error("Autocomplete error:", err);
      setClients([]);
    }
    setLoading(false);
  }, []);
  
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchClients(newValue), 250);
  };
  
  const selectClient = (client) => {
    if (onClientSelect) {
      onClientSelect(client.row_number, client.client_id);
    }
    setShowDropdown(false);
  };

  const selectAsFilter = (client) => {
    onChange(client.nom);
    setShowDropdown(false);
  };
  
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (inputRef.current && !inputRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  return (
    <div className="relative" ref={inputRef}>
      <input
        type="text"
        data-testid={testId}
        value={value}
        onChange={handleInputChange}
        onFocus={() => clients.length > 0 && setShowDropdown(true)}
        placeholder={placeholder}
        className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
      />
      {loading && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <RefreshCw className="w-4 h-4 text-slate-400 animate-spin" />
        </div>
      )}
      
      {showDropdown && clients.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl max-h-80 overflow-y-auto" style={{minWidth: "360px"}}>
          {clients.map((client, idx) => (
            <div
              key={idx}
              className="px-3 py-2.5 hover:bg-purple-600/20 transition-colors border-b border-slate-700/50 last:border-b-0 cursor-pointer group"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0" onClick={() => selectClient(client)}>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-white text-sm">{client.nom}</span>
                    <span className="text-slate-300 text-sm">{client.prenom}</span>
                    {client.dpt && <span className="text-xs bg-slate-700 text-slate-400 px-1.5 py-0.5 rounded">{client.dpt}</span>}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    {client.telephone && (
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <Phone className="w-3 h-3" />{client.telephone}
                      </span>
                    )}
                    {client.ville && (
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />{client.ville}
                      </span>
                    )}
                  </div>
                  {client.etape_nom && (
                    <span className="text-xs text-purple-400 mt-0.5 block truncate">{client.etape_nom}</span>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={(e) => { e.stopPropagation(); selectAsFilter(client); }}
                    className="p-1 hover:bg-slate-600 rounded text-slate-500 hover:text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Utiliser comme filtre"
                  >
                    <Filter className="w-3 h-3" />
                  </button>
                  <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-purple-400" onClick={() => selectClient(client)} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FilterSelect({ filterKey, filterData, currentValue, onChange }) {
  if (!filterData) return null;
  
  const options = filterData.values || [];
  const label = filterData.label || filterKey;
  
  return (
    <div>
      <label className="block text-xs text-slate-400 uppercase tracking-wider mb-1">
        {label}
      </label>
      <select
        data-testid={`filter-${filterKey}`}
        value={currentValue || ""}
        onChange={(e) => onChange(filterKey, e.target.value)}
        className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
      >
        <option value="">Tous</option>
        {options.map((v) => (
          <option key={v} value={v}>{v}</option>
        ))}
      </select>
    </div>
  );
}

function ResultRow({ item, columnList, onRowClick, onArchive, onDelete, isAdmin }) {
  const rowNum = item.row_number;
  const clientId = item.client_id;
  const rowData = item.data;
  
  return (
    <tr
      className="hover:bg-slate-700/50 transition-colors cursor-pointer group"
      onClick={() => onRowClick(rowNum, clientId)}
    >
      <td className="px-3 py-3 sticky left-0 bg-slate-800 group-hover:bg-slate-700/50 z-10">
        <div className="flex items-center gap-1">
          <button
            data-testid={`view-client-${rowNum}`}
            onClick={(e) => { e.stopPropagation(); onRowClick(rowNum, clientId); }}
            className="p-1.5 hover:bg-purple-600/30 rounded-lg transition-colors"
            title="Voir la fiche"
          >
            <Eye className="w-4 h-4 text-purple-400" />
          </button>
          {isAdmin && (
            <>
              <button
                data-testid={`archive-client-${rowNum}`}
                onClick={(e) => { e.stopPropagation(); onArchive(rowNum, rowData); }}
                className="p-1.5 hover:bg-amber-600/20 rounded-lg transition-colors"
                title="Archiver"
              >
                <Archive className="w-4 h-4 text-amber-400" />
              </button>
              <button
                data-testid={`delete-client-${rowNum}`}
                onClick={(e) => { e.stopPropagation(); onDelete(rowNum, rowData); }}
                className="p-1.5 hover:bg-red-600/20 rounded-lg transition-colors"
                title="Supprimer"
              >
                <Trash2 className="w-4 h-4 text-red-400" />
              </button>
            </>
          )}
        </div>
      </td>
      {columnList.map((c) => {
        const cellValue = rowData[c.title] || "";
        return (
          <td key={c.index} className="px-4 py-3 text-sm whitespace-nowrap">
            <span className="truncate block max-w-[200px]" title={cellValue}>
              {cellValue || <span className="text-slate-600">—</span>}
            </span>
          </td>
        );
      })}
    </tr>
  );
}

function SearchPage({ onBack, onClientClick }) {
  const [filters, setFilters] = useState({});
  const [filterOptions, setFilterOptions] = useState({});
  const [results, setResults] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  
  // Champs texte pour nom et prénom
  const [nomSearch, setNomSearch] = useState("");
  const [prenomSearch, setPrenomSearch] = useState("");

  // Admin check
  const isAdmin = (() => {
    try {
      const user = JSON.parse(localStorage.getItem("auth_user") || "{}");
      return user.role === "admin" || user.role === "super_admin" || user.is_platform_owner === true;
    } catch { return false; }
  })();

  useEffect(() => {
    axios.get(`${API}/search/filters`)
      .then(res => setFilterOptions(res.data))
      .catch(err => console.error("Error loading filters:", err));
  }, []);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters };
    if (value) {
      newFilters[key] = value;
    } else {
      delete newFilters[key];
    }
    setFilters(newFilters);
  };

  const handleSearch = () => {
    setLoading(true);
    setSearched(true);
    const searchFilters = { ...filters };
    if (nomSearch.trim()) searchFilters.text_search = nomSearch.trim();
    if (prenomSearch.trim()) searchFilters.prenom = prenomSearch.trim();
    
    axios.post(`${API}/search`, searchFilters)
      .then(res => {
        setResults(res.data.clients || []);
        setColumns(res.data.columns || []);
      })
      .catch(err => console.error("Error searching:", err))
      .finally(() => setLoading(false));
  };

  const handleArchive = async (rowNum, rowData) => {
    const clientName = `${rowData["NOM"] || rowData["Nom"] || ""} ${rowData["PRENOM"] || rowData["Prénom"] || ""}`.trim();
    if (!window.confirm(`Archiver ${clientName || "ce client"} (row ${rowNum}) ? Le client ne sera plus visible dans les dashboards.`)) return;
    try {
      await axios.post(`${API}/clients/${rowNum}/archive`);
      setResults(prev => prev.filter(r => r.row_number !== rowNum));
    } catch (e) {
      alert("Erreur: " + (e.response?.data?.detail || e.message));
    }
  };

  const handleDelete = async (rowNum, rowData) => {
    const clientName = `${rowData["NOM"] || rowData["Nom"] || ""} ${rowData["PRENOM"] || rowData["Prénom"] || ""}`.trim();
    if (!window.confirm(`SUPPRIMER DEFINITIVEMENT ${clientName || "ce client"} (row ${rowNum}) ? Cette action est irreversible.`)) return;
    if (!window.confirm(`Etes-vous absolument sur ? Cette suppression ne peut pas etre annulee.`)) return;
    try {
      await axios.delete(`${API}/clients/${rowNum}`);
      setResults(prev => prev.filter(r => r.row_number !== rowNum));
    } catch (e) {
      alert("Erreur: " + (e.response?.data?.detail || e.message));
    }
  };

  const handleReset = () => {
    setFilters({});
    setNomSearch("");
    setPrenomSearch("");
    setResults([]);
    setSearched(false);
  };

  const filterKeyList = Object.keys(filterOptions);
  const activeCount = Object.keys(filters).length + (nomSearch ? 1 : 0) + (prenomSearch ? 1 : 0);

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <header className="bg-slate-950 border-b border-slate-800 px-6 py-4">
        <div className="flex items-center gap-4">
          <button
            data-testid="back-to-poles-btn"
            onClick={onBack}
            className="p-2 hover:bg-slate-800 rounded-lg"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
              <Search className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg">Recherche dossier multi-critères</h1>
              <p className="text-xs text-slate-400">Trouvez des dossiers en combinant plusieurs filtres</p>
            </div>
          </div>
        </div>
      </header>

      <div className="p-6">
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-white flex items-center gap-2">
              <Filter className="w-5 h-5 text-purple-400" />
              Critères de recherche
            </h2>
            {activeCount > 0 && (
              <span className="bg-purple-600 text-white text-xs px-2 py-1 rounded-full">
                {activeCount} filtre{activeCount > 1 ? "s" : ""} actif{activeCount > 1 ? "s" : ""}
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {/* Champs texte Nom et Prénom avec autocomplétion */}
            <div>
              <label className="block text-xs text-slate-400 uppercase tracking-wider mb-1">
                Nom / Prenom / Tel
              </label>
              <AutocompleteInput
                value={nomSearch}
                onChange={setNomSearch}
                placeholder="Tapez un nom, prenom ou telephone..."
                testId="filter-nom-text"
                onClientSelect={onClientClick}
              />
            </div>
            
            {/* Filtres dropdown existants (exclure nom et prenom car on a les champs texte) */}
            {filterKeyList
              .filter(fk => fk !== 'nom' && fk !== 'prenom')
              .map((fk) => (
              <FilterSelect
                key={fk}
                filterKey={fk}
                filterData={filterOptions[fk]}
                currentValue={filters[fk]}
                onChange={handleFilterChange}
              />
            ))}
          </div>

          <div className="flex gap-3 mt-6">
            <button
              data-testid="execute-search-btn"
              onClick={handleSearch}
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white font-bold py-3 px-6 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
              <span>{loading ? "Recherche..." : "Rechercher"}</span>
            </button>
            <button
              data-testid="reset-search-btn"
              onClick={handleReset}
              className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-lg flex items-center gap-2"
            >
              <X className="w-5 h-5" />
              <span>Réinitialiser</span>
            </button>
          </div>
        </div>

        {searched && (
          <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
              <h2 className="font-bold text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-400" />
                Résultats
              </h2>
              <span className="text-slate-400 text-sm">
                {results.length} dossier{results.length !== 1 ? "s" : ""} trouvé{results.length !== 1 ? "s" : ""}
              </span>
            </div>

            {results.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-900">
                    <tr>
                      <th className="sticky left-0 bg-slate-900 z-10 px-3 py-3 text-xs uppercase tracking-wider text-slate-400 font-medium">Actions</th>
                      {columns.map((c) => (
                        <th key={c.index} className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-400 font-medium whitespace-nowrap">
                          {c.title}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {results.map((r) => (
                      <ResultRow key={r.row_number} item={r} columnList={columns} onRowClick={onClientClick} onArchive={handleArchive} onDelete={handleDelete} isAdmin={isAdmin} />
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 text-slate-400">
                {loading ? (
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto" />
                ) : (
                  <div>
                    <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                    <p>Aucun dossier trouvé avec ces critères</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {!searched && (
          <div className="text-center py-16 text-slate-400">
            <Search className="w-16 h-16 mx-auto mb-4 text-slate-600" />
            <p className="text-lg">Sélectionnez vos critères et cliquez sur "Rechercher"</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default SearchPage;
